'use client';

import * as React from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { RichPopover } from '../primitives/RichPopover';
import { TeaseUpsell, type TeaseUpsellProps } from './TeaseUpsell';

export interface TeaseSurfaceProps {
  /** The blurred / placeholder element the user can hover (desktop). */
  children: React.ReactNode;
  /** Desktop popover header + accessible label. */
  title: string;
  /** Body copy shown inside the desktop popover (1 short sentence). */
  body: string;
  ctaLabel?: TeaseUpsellProps['ctaLabel'];
  ctaHref?: TeaseUpsellProps['ctaHref'];
  secondaryLabel?: TeaseUpsellProps['secondaryLabel'];
  secondaryHref?: TeaseUpsellProps['secondaryHref'];
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Wrapper class applied to the trigger span on desktop (and the bare wrapper on mobile). */
  wrapperClassName?: string;
  /** Analytics label for where this prompt appeared — see TeaseUpsell. */
  surface?: string;
}

/**
 * Dispatches the "hidden EV / hidden projection" tease surface by viewport:
 *  - Desktop (≥768px): RichPopover with TeaseUpsell body (headline + CTA button + secondary link).
 *  - Mobile (<768px): renders children unwrapped — no tooltip, no drawer. The mobile CTA path
 *    lives elsewhere (e.g. a TeaseBanner inside the player-card expansion), so we deliberately
 *    don't attach an interactive surface to every blurred edge.
 */
export const TeaseSurface: React.FC<TeaseSurfaceProps> = ({
  children,
  title,
  body,
  ctaLabel,
  ctaHref,
  secondaryLabel,
  secondaryHref,
  side = 'top',
  wrapperClassName,
  surface,
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <RichPopover
        trigger={<>{children}</>}
        header={<span className="text-sm font-semibold">{title}</span>}
        ariaLabel={title}
        width={260}
        side={side}
        wrapperClassName={wrapperClassName}
        openDelayMs={450}
      >
        <TeaseUpsell
          title={title}
          body={body}
          ctaLabel={ctaLabel}
          ctaHref={ctaHref}
          secondaryLabel={secondaryLabel}
          secondaryHref={secondaryHref}
          surface={surface}
        />
      </RichPopover>
    );
  }

  return wrapperClassName ? <div className={wrapperClassName}>{children}</div> : <>{children}</>;
};
